import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, Copy, Check, Webhook } from 'lucide-react';
import { api } from '@/api/client';

interface Courier { id: number; name: string; code: string; brand?: string }

// Per-courier API endpoint config — stored in Courier.config.api
interface ApiConfig {
  tokenUrl?: string;
  createWarehouseUrl?: string;
  editWarehouseUrl?: string;
  bookingUrl?: string;             // waybill / booking
  trackingUrl?: string;             // order tracking pull
  cancelUrl?: string;
  editOrderUrl?: string;            // edit waybill / orders
  ndrActionUrl?: string;
  appointmentUrl?: string;
  // free-form credentials block (merged into encrypted CourierCredential later)
  credentials?: Record<string, string>;
}

// Generated push tracking webhook the courier should POST status updates to.
// In real deployment this lives in BACKEND env (BACKEND_PUBLIC_URL) — for now we
// expose the path so admin can copy it to the courier portal.
const WEBHOOK_PATH_TEMPLATE = '/v1/webhooks/courier/:code/push';

export function CourierApiConfigDialog({ courier, onClose }: { courier: Courier; onClose: () => void }) {
  const qc = useQueryClient();
  const [cfg, setCfg] = useState<ApiConfig>({});
  const [credText, setCredText] = useState('{}');
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['courier-full', courier.id],
    queryFn: async () => (await api.get<{ config?: any }>(`/couriers/${courier.id}`)).data,
  });

  useEffect(() => {
    const fromServer = (data?.config?.api ?? {}) as ApiConfig;
    setCfg(fromServer);
    setCredText(JSON.stringify(fromServer.credentials ?? {}, null, 2));
  }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      let credentials: Record<string, string> | undefined;
      if (credText.trim()) {
        try { credentials = JSON.parse(credText); }
        catch (e: any) { throw new Error('Credentials JSON is invalid: ' + e.message); }
      }
      const nextConfig = {
        ...(data?.config ?? {}),
        api: { ...cfg, credentials },
      };
      return (await api.patch(`/couriers/${courier.id}`, { config: nextConfig })).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['courier-full', courier.id] }); qc.invalidateQueries({ queryKey: ['couriers'] }); toast.success('API config saved'); },
    onError: (e: any) => toast.error(e?.message ?? e?.response?.data?.message ?? 'Save failed'),
  });

  const webhookUrl = (typeof window !== 'undefined' ? window.location.origin.replace(/web-/, 'backend-') : '') + WEBHOOK_PATH_TEMPLATE.replace(':code', courier.code);
  const copyWebhook = () => { navigator.clipboard.writeText(webhookUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const update = (patch: Partial<ApiConfig>) => setCfg(c => ({ ...c, ...patch }));

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5 max-h-[75vh] overflow-y-auto pr-2">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
        Per-courier API endpoints. Filled URLs are called from the order booking, tracking, NDR and appointment flows.
        Credentials are stored as JSON (will be AES-encrypted in <code>courier_credentials</code> for multi-account setups).
      </div>

      <Section title="Auth">
        <UrlField label="Token / Login URL" value={cfg.tokenUrl} placeholder="https://api.example.com/auth/token"
          onChange={(v) => update({ tokenUrl: v })} />
      </Section>

      <Section title="Warehouse / Pickup Address">
        <UrlField label="Create Warehouse URL" value={cfg.createWarehouseUrl}
          placeholder="https://api.example.com/warehouse/create" onChange={(v) => update({ createWarehouseUrl: v })} />
        <UrlField label="Edit Warehouse URL" value={cfg.editWarehouseUrl}
          placeholder="https://api.example.com/warehouse/edit" onChange={(v) => update({ editWarehouseUrl: v })} />
      </Section>

      <Section title="Waybill / Order Booking">
        <UrlField label="Booking / Assign Waybill URL" value={cfg.bookingUrl}
          placeholder="https://dtdcapi.shipsy.io/api/customer/integration/consignment/softdata"
          onChange={(v) => update({ bookingUrl: v })} />
        <UrlField label="Edit Waybill / Order URL" value={cfg.editOrderUrl}
          placeholder="https://api.example.com/order/edit" onChange={(v) => update({ editOrderUrl: v })} />
        <UrlField label="Cancel Waybill URL" value={cfg.cancelUrl}
          placeholder="http://dtdcapi.shipsy.io/api/customer/integration/consignment/cancel"
          onChange={(v) => update({ cancelUrl: v })} />
      </Section>

      <Section title="Tracking">
        <UrlField label="Pull Tracking URL (status fetch)" value={cfg.trackingUrl}
          placeholder="https://blktracksvc.dtdc.com/dtdc-api/rest/JSONCnTrk/getTrackDetails"
          onChange={(v) => update({ trackingUrl: v })} />
        <div className="border border-dashed border-emerald-300 bg-emerald-50/50 rounded-lg p-3">
          <div className="text-xs font-medium text-emerald-700 mb-1 flex items-center gap-1"><Webhook size={12} /> Push Tracking Webhook</div>
          <p className="text-xs text-slate-600 mb-2">
            Paste this URL into the courier's portal so they POST status updates to us in real time.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white px-2 py-1.5 rounded border border-slate-200 break-all">{webhookUrl}</code>
            <button type="button" onClick={copyWebhook}
              className="px-2 py-1.5 border border-slate-300 rounded hover:bg-slate-50 text-xs flex items-center gap-1">
              {copied ? <><Check size={12} className="text-success" /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
          </div>
        </div>
      </Section>

      <Section title="NDR / Appointment">
        <UrlField label="NDR Action URL" value={cfg.ndrActionUrl}
          placeholder="https://api.example.com/ndr/action" onChange={(v) => update({ ndrActionUrl: v })} />
        <UrlField label="Book Appointment URL" value={cfg.appointmentUrl}
          placeholder="https://api.example.com/appointment/book" onChange={(v) => update({ appointmentUrl: v })} />
      </Section>

      <Section title="Credentials (JSON)">
        <p className="text-xs text-slate-500 mb-2">
          Free-form key/value credentials. Keys vary per courier — e.g. for DTDC: <code>api_key</code>, <code>code</code>, <code>username</code>, <code>password</code>, <code>tracking_token</code>.
          For Delhivery: <code>token</code>, <code>client</code>, <code>user</code>.
        </p>
        <textarea value={credText} onChange={(e) => setCredText(e.target.value)} rows={8}
          className="input font-mono text-xs" placeholder='{ "api_key": "...", "username": "..." }' />
      </Section>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 sticky bottom-0 bg-white pb-1">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Close</button>
        <button type="button" onClick={() => save.mutate()} disabled={save.isPending}
          className="px-4 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2">
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save API Config
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {children}
    </div>
  );
}

function UrlField({ label, value, placeholder, onChange }: { label: string; value?: string; placeholder?: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="input font-mono text-xs" />
    </div>
  );
}
